import { Injectable, inject } from '@angular/core';
import { NgKLoggerService } from '@kin/ngk-logger';
import { LOCAL_STORAGE } from '@ng-web-apis/common';
import { Observable, catchError, firstValueFrom, forkJoin, map, of } from 'rxjs';

import { GoogleTagManagerService } from 'app/common/services/analytics/google-tagmanager/gtm.service';
import { QuoteFormOptionsStore } from 'app/global-store/quote-form-options';

import { LegacyAnswersApiService } from '../../../common/services/legacy/answers/answers.service';
import { LegacyCreateCirService } from '../../../common/services/legacy/create-cir/create-cir.service';
import type { LegacyApiPagesResponse } from '../../../common/services/legacy/quote-pages';
import { LegacyPagesApiService } from '../../../common/services/legacy/quote-pages';
import { mapLegacyAnswersToQuoteDraftStore } from '../../../common/services/legacy/response-mapping/map-legacy-answers-to-quote-draft-store';
import { mapLegacyUsersToQuoteDraftStore } from '../../../common/services/legacy/response-mapping/map-legacy-users-to-quote-draft-store';
import { LegacyUsersApiService } from '../../../common/services/legacy/users/users.service';
import { QuoteDraftState, QuoteDraftStore } from '../../../global-store/quote-draft';
import { UserStore } from '../../../global-store/user/user.store';
import { LOCAL_STORAGE_DRAFT_TOKEN_KEY, QuoteDraftEntryRouteTo } from '../entry/quote-draft-entry.service';

export type RestoreQuoteDraft = {
  entryRoute: QuoteDraftEntryRouteTo;
  restoredState: Partial<QuoteDraftState>;
  pageResponse?: LegacyApiPagesResponse;
};

/**
 * Quote Draft Sync Service
 * - ability to sync UI state to the API (for debugging)
 * - ability to sync API state with UI (aka resume)
 */
@Injectable({
  providedIn: 'root',
})
export class QuoteDraftSyncService {
  private answersService = inject(LegacyAnswersApiService);
  private userService = inject(LegacyUsersApiService);
  private pagesService = inject(LegacyPagesApiService);
  private createCirService = inject(LegacyCreateCirService);
  private gtmService = inject(GoogleTagManagerService);
  private localStorage = inject(LOCAL_STORAGE);
  private userStore = inject(UserStore);
  private quoteDraftStore = inject(QuoteDraftStore);
  private logger = inject(NgKLoggerService);
  private optionsStore = inject(QuoteFormOptionsStore);

  private segmentUserLoaded = false;

  /**
   * Handle direct access to the about-you page with a token
   * This is used when users try to access the about-you page directly from click leads
   * without going through the landing page first
   *
   * @param token The token from the query params
   * @returns A Promise that resolves to true once setup is complete, even if prefilling fails
   */
  public async handleDirectAboutYouAccess(token: string): Promise<boolean> {
    if (!token) {
      return false;
    }

    // Store the token in localStorage and update the store
    this.localStorage.setItem(LOCAL_STORAGE_DRAFT_TOKEN_KEY, token);
    this.quoteDraftStore.patchStore({ draftToken: token });

    // Set up the user state to allow access
    this.userStore.setEntryType('click-lead');
    this.userStore.startQuoting();

    try {
      // Attempt to prefill the form with user data, but continue even if it fails
      const quoteToRestore = await firstValueFrom(this.restoreStoreForClickLead());
      this.quoteDraftStore.patchStore(quoteToRestore);
    } catch {
      this.logger.error({
        context: 'QuoteDraftSyncService.handleDirectAboutYouAccess',
        message: `Unable to prefill About You page from click lead direct access`,
        priority: 'P4',
      });
    }

    // Always return true since we want to allow access even if prefilling fails
    return true;
  }

  /**
   * Gets the next page and page options using a token.
   * Waits for next page and next page options to be loaded before allowing navigation.
   *
   * @param token The token to use for retrieving page information
   * @param disallowBack Whether to disallow backwards navigation in the quoting flow
   * @returns A Promise that resolves to the page response when setup is complete
   */
  public async getNextPageAndPageOptions(token: string, disallowBack: boolean): Promise<LegacyApiPagesResponse | undefined> {
    // Store the token from localStorage in the quoteDraftStore
    this.quoteDraftStore.patchStore({ draftToken: token });

    // Disallow backwards navigation. This prevents users from seeing unhydrated pages that
    // rely on dot-com responses from /GET pages that are no longer available since the user
    // is past that page in the quoting flow now.
    this.quoteDraftStore.patchStore({ shouldDisallowBackwardsNavigation: disallowBack });

    this.userStore.startQuoting();

    try {
      // Fetch the next page from dot-com and wait for the result.
      const pageResponse = await firstValueFrom(this.pagesService.pollForNextPage());

      if (pageResponse.page?.user_exists === true) {
        sessionStorage.setItem('user_exists', 'true');
      }

      // Set the next page's options in the quoteFormOptionsStore.
      if (pageResponse.page) {
        const formOptions = this.optionsStore.getLegacyOptionsForForm(pageResponse.page.path, pageResponse.page.components, this.logger);
        this.optionsStore.updateOptionsForFormByLegacyPath(pageResponse.page.path, formOptions, this.logger);
        return pageResponse;
      }

      return pageResponse;
    } catch (error) {
      this.logger.error({
        context: 'QuoteDraftSyncService.getNextPageAndPageOptions',
        message: 'Failed to retrieve page',
        priority: 'P4',
        additionalInfo: {
          error,
        },
      });
    }

    return undefined;
  }

  /**
   * A token needs to be set in the store for this to work, if it is not the API will reject the request
   *
   * The user service may error if called prior to user creation (before about you page submission)
   * so we catch errors for that one. This should only happen if a user tries to resume a quote
   * that only has an address entered.
   */
  public restoreStore(): Observable<RestoreQuoteDraft> {
    return forkJoin([
      this.answersService.getAnswersApi().pipe(
        catchError((error) => {
          this.logger.error({ message: error, context: 'QuoteDraftSyncService::restoreStore::getAnswersApi', priority: 'P3' });
          return of(undefined);
        }),
      ),
      this.userService.getUserFromApi().pipe(
        catchError((error) => {
          this.logger.error({ message: error, context: 'QuoteDraftSyncService::restoreStore::getUserFromApi', priority: 'P3' });
          return of(undefined);
        }),
      ),
      this.pagesService.pollForNextPage().pipe(
        catchError((error) => {
          this.logger.error({ message: error, context: 'QuoteDraftSyncService::restoreStore::pollForNextPage', priority: 'P3' });
          return of(undefined);
        }),
      ),
    ]).pipe(
      map(([answersRes, userRes, pgRes]) => {
        let entryRoute!: QuoteDraftEntryRouteTo;
        let restoredState: Partial<QuoteDraftState> = mapLegacyAnswersToQuoteDraftStore(answersRes ?? {}) || {};
        let pageResponse!: LegacyApiPagesResponse | undefined;
        if (userRes) {
          restoredState = { ...restoredState, ...(mapLegacyUsersToQuoteDraftStore(userRes) || {}) };
        }
        if (userRes && !this.segmentUserLoaded) {
          this.gtmService.setKinId(userRes.kin_id);
          this.gtmService.setKinPersonId(userRes.kin_person_id);
          this.gtmService.setKinSegmentId(userRes.kin_segment_user_id);
          this.segmentUserLoaded = true;
        }
        if (pgRes?.page) {
          pageResponse = pgRes;
          entryRoute = { type: 'form', route: pgRes.page.path };
        }
        if (pgRes?.redirect_to) {
          entryRoute = { type: 'redirect', route: pgRes.redirect_to };
        }
        return { entryRoute, restoredState, pageResponse };
      }),
    );
  }

  /**
   * This is a special case for click leads, we only need to retrieve data from the /answers endpoint
   * which provides data from the CustomerInput::Repsonse model (CIR). This is so we can prefill
   * the form fields on the page that the user is redirected to (which at the time of writing this is only the About You Page).
   *
   * There is no User to retrieve data from for Click Leads.
   *
   * There is no need to retrieve the next page from the PagesController since the ClickLeadsController returns the redirect URL.
   */
  public restoreStoreForClickLead(): Observable<Partial<QuoteDraftState>> {
    return this.answersService.getAnswersApi().pipe(
      map((answersRes) => {
        return mapLegacyAnswersToQuoteDraftStore(answersRes) || {};
      }),
    );
  }

  /**
   * Gets the standardized address from `Api::V1` and updates the `quoteDraftStore`
   * with the standardized address if the call is successful.
   */
  public async getStandardizedAddress(): Promise<void> {
    try {
      const response = await firstValueFrom(this.createCirService.getAddress());

      if (response.address?.standardized_address) {
        this.quoteDraftStore.patchStore({
          displayAddress: response.address.standardized_address,
        });
      }

      if (response.address) {
        const currentAddress = this.quoteDraftStore.address();
        this.quoteDraftStore.patchStore({
          address: { ...currentAddress!, state: response.address.state },
        });
      }
    } catch (error) {
      this.logger.error({
        context: 'QuoteDraftSyncService.getStandardizedAddress',
        message: 'Failed to get standardized address',
        priority: 'P4',
        additionalInfo: {
          error,
        },
      });
    }
  }
}
